// @flow
import { LOCATION_CHANGE } from "connected-react-router";
import { startRequest, finishRequest } from "@store/utils";

import {
  GetAbsenceRateTrendsDefaultReports,
  GetAbsenceRateTrendsDefaultReportsSuccess,
  GetAbsenceRateTrendsDefaultReportsError,
  GetAbsenceRateTrendsMyReports,
  GetAbsenceRateTrendsMyReportsSuccess,
  GetAbsenceRateTrendsMyReportsError,
  GetAbsenceRateTrendsReportData,
  GetAbsenceRateTrendsReportDataSuccess,
  GetAbsenceRateTrendsReportDataError,
  GetAbsenceRateTrendsReportSchemaSuccess,
  GetAbsenceRateTrendsReportSchemaError,
  SetAbsenceRateTrendsReportToSave,
  SetAbsenceRateTrendsReportViewState,
  ExportAbsenceRateTrendsReport,
  ExportAbsenceRateTrendsReportSuccess,
  ExportAbsenceRateTrendsReportError,
} from "@modules/reporting/constants/actions";
import type { AbsenceRateState, AbsenceRateReportViewState } from "./types";
import { cloneObject } from "@utils/object";

export const initialViewState: AbsenceRateReportViewState = {
  filtersExpanded: false,
  chartsExpanded: true,
  reportTableExpanded: true,
  showExclusionsModal: false,
};

export const initialState: AbsenceRateState = {
  error: null,
  requestsInProgress: 0,
  reportGenerateInProgress: 0,
  defaultReports: [],
  myReports: [],
  report: {
    details: null,
    reportData: {
      absenceReasonExclusions: [],
      details: [],
      headlines: {
        periods: [],
        total: 0,
        name: "",
      },
      targetAbsenceRate: 0,
    },
  },
  schema: null,
  reportToSave: null,
  viewState: initialViewState,
};

export const setLoading = (state: AbsenceRateState) => () => startRequest(state);

export const onError = (state: AbsenceRateState) => ({ payload }: Object) => {
  return {
    ...finishRequest(state),
    error: payload,
  };
};

export const onGetAbsenceRateTrendsDefaultReportsSuccess = (state: AbsenceRateState) => ({
  payload,
}: Object) => {
  return {
    ...finishRequest(state),
    error: null,
    defaultReports: payload,
  };
};

export const onGetAbsenceRateTrendsMyReportsSuccess = (state: AbsenceRateState) => ({
  payload,
}: Object) => {
  return {
    ...finishRequest(state),
    error: null,
    myReports: payload,
  };
};

export const onGetAbsenceRateTrendsReportDataSuccess = (state: AbsenceRateState) => ({
  payload,
}: Object) => {
  return {
    ...finishRequest(state),
    error: null,
    report: payload,
  };
};
export const onGetAbsenceRateTrendsReportSchemaSuccess = (state: AbsenceRateState) => ({
  payload,
}: Object) => {
  return {
    ...state,
    schema: payload,
  };
};

export const onGetAbsenceRateTrendsReportSchemaError = (state: AbsenceRateState) => ({
  payload,
}: Object) => {
  return {
    ...state,
    error: payload,
  };
};

export const onSetAbsenceRateTrendsReportToSave = (state: AbsenceRateState) => ({
  payload,
}: Object) => {
  return {
    ...state,
    reportToSave: payload,
  };
};

export const onSetViewState = (state: AbsenceRateState) => ({ payload }: Object) => {
  return {
    ...state,
    viewState: payload,
  };
};

export const onResetViewState = (state: AbsenceRateState) => () => ({
  ...state,
  viewState: initialViewState,
});

export const onGetAbsenceRateTrendsReportData = (state: AbsenceRateState) => () => {
  // need to reset report data, because old data i still generate wrong data
  return {
    ...startRequest(state),
    report: {
      details: null,
      reportData: {
        headlines: {
          periods: [],
          total: 0,
          name: "",
        },
        targetAbsenceRate: 0,
        details: [],
      },
    },
  };
};

export const onExportAbsenceRateTrendsReport = (state: AbsenceRateState) => () => {
  return {
    ...state,
    reportGenerateInProgress: ++state.reportGenerateInProgress,
  };
};

export const onExportAbsenceRateTrendsReportLoaded = (state: AbsenceRateState) => () => {
  return {
    ...state,
    reportGenerateInProgress:
      state.reportGenerateInProgress > 0 ? --state.reportGenerateInProgress : 0,
  };
};

export default (state: AbsenceRateState = initialState, action: Object): AbsenceRateState => {
  const act = {
    [ExportAbsenceRateTrendsReport]: onExportAbsenceRateTrendsReport,
    [ExportAbsenceRateTrendsReportSuccess]: onExportAbsenceRateTrendsReportLoaded,
    [ExportAbsenceRateTrendsReportError]: onExportAbsenceRateTrendsReportLoaded,
    [GetAbsenceRateTrendsDefaultReports]: setLoading,
    [GetAbsenceRateTrendsDefaultReportsSuccess]: onGetAbsenceRateTrendsDefaultReportsSuccess,
    [GetAbsenceRateTrendsDefaultReportsError]: onError,
    [GetAbsenceRateTrendsMyReports]: setLoading,
    [GetAbsenceRateTrendsMyReportsSuccess]: onGetAbsenceRateTrendsMyReportsSuccess,
    [GetAbsenceRateTrendsMyReportsError]: onError,
    [GetAbsenceRateTrendsReportData]: onGetAbsenceRateTrendsReportData,
    [GetAbsenceRateTrendsReportDataSuccess]: onGetAbsenceRateTrendsReportDataSuccess,
    [GetAbsenceRateTrendsReportDataError]: onError,
    [GetAbsenceRateTrendsReportSchemaSuccess]: onGetAbsenceRateTrendsReportSchemaSuccess,
    [GetAbsenceRateTrendsReportSchemaError]: onGetAbsenceRateTrendsReportSchemaError,
    [SetAbsenceRateTrendsReportToSave]: onSetAbsenceRateTrendsReportToSave,
    [SetAbsenceRateTrendsReportViewState]: onSetViewState,
    [LOCATION_CHANGE]: onResetViewState,
  };
  if (action && action.type && act && act[action.type]) {
    return act[action.type](cloneObject(state))(action);
  }
  return state;
};
